import { createContext, useCallback, useState, type ReactNode } from "react";

import { isEqual, isNil } from "lodash";
import LZString from "lz-string";
import {
  createEnumParam,
  decodeJson,
  encodeJson,
  StringParam,
  withDefault,
  type QueryParamConfig,
} from "serialize-query-params";
import { useQueryParams } from "use-query-params";

import { useUser } from "@components/user-context";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import {
  CustomJsonCompressedParam,
  CustomJsonParam,
  MCAttributionWindowDays,
  NBAccountingMode,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import {
  type ChartView,
  type MetricFilter,
  type ProductAnalyticsContextDescriptor,
  type Sorting,
  type TabView,
} from "./types";

const AccountingModeParam = createEnumParam<INBAccountingMode>(
  NBAccountingMode.keyOrder,
);

const AttributionWindowParam = createEnumParam<IMCAttributionWindowDays>(
  MCAttributionWindowDays.keyOrder,
);

const AdvancedSearchParam = CustomJsonCompressedParam<string | null>();

const SortingParam = CustomJsonCompressedParam<Sorting>();

const MetricFilterParam = CustomJsonCompressedParam<MetricFilter[]>();

const ChartViewParam = createEnumParam<ChartView>(["line", "bar"]);
const StringParamType = CustomJsonCompressedParam<string>();

const TabViewParam = createEnumParam<TabView>([
  "category",
  "product",
  "platform",
  "ad",
  "campaign",
]);

export const DEFAULT_SORTING: Sorting = { id: "rev", order: "desc" };

export const DEFAULT_FILTER: MetricFilter[] = [
  {
    id: "rev",
    operator: "greaterThan",
    value: 0,
  },
  {
    id: "proportional_spend",
    operator: "greaterThan",
    value: 0,
  },
  {
    id: "txns",
    operator: "greaterThan",
    value: 0,
  },
];

export const ProductAnalyticsContext =
  createContext<ProductAnalyticsContextDescriptor | null>(null);

const DateRangeParam: QueryParamConfig<DateRangeChoiceV3 | FixedDateInterval> =
  withDefault(
    CustomJsonParam<DateRangeChoiceV3 | FixedDateInterval>(),
    "last30Days" as DateRangeChoiceV3,
  );

const SelectedIdsParam = (): QueryParamConfig<string[]> => ({
  encode: (value) => {
    try {
      const stringified = encodeJson(value)!;
      return LZString.compressToEncodedURIComponent(stringified);
    } catch (error) {
      return "";
    }
  },
  decode: (value) => {
    if (isNil(value)) {
      return [];
    }

    try {
      return decodeJson(
        LZString.decompressFromEncodedURIComponent(value as string),
      );
    } catch (error) {
      return [];
    }
  },
  equals: (a, b) => isEqual(a, b),
});

export const ProductAnalyticsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { user } = useUser();

  const [chartSelected, setChartSelected] = useState<string[]>([]);
  const [productItemRelationModalOpen, setProductItemRelationModalOpen] =
    useState<boolean>(false);
  const [selectedRelationRow, setSelectedRelationRow] = useState<any | null>(
    null,
  );

  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();

  const [filters, setFilters] = useQueryParams(
    {
      attributionModel: withDefault(StringParam, attributionModel),
      attributionWindow: withDefault(AttributionWindowParam, attributionWindow),
      accountingMode: withDefault(AccountingModeParam, accountingMode),
      dateRange: withDefault(DateRangeParam, "last30Days" as const),
      advancedSearch: withDefault(AdvancedSearchParam, ""),
    },
    { updateType: "pushIn" },
  );

  const [tableState, setTableState] = useQueryParams(
    {
      sorting: withDefault(SortingParam, { ...DEFAULT_SORTING }),
      metricFilters: withDefault(MetricFilterParam, [
        ...DEFAULT_FILTER,
      ] as const),
      selectedProducts: withDefault(SelectedIdsParam(), [] as const),
      selectedPlatforms: withDefault(SelectedIdsParam(), [] as const),
      selectedCampaigns: withDefault(SelectedIdsParam(), [] as const),
      selectedAds: withDefault(SelectedIdsParam(), [] as const),
    },
    { updateType: "pushIn" },
  );

  const [chartState, setChartState] = useQueryParams(
    {
      chartView: withDefault(ChartViewParam, "bar" as const),
    },
    { updateType: "pushIn" },
  );

  const [selectedQuickSelect, setSelectedQuickSelect] = useQueryParams(
    {
      quickSelect: withDefault(StringParamType, "" as const),
    },
    { updateType: "pushIn" },
  );

  const setSelectedProducts = useCallback(
    (value: string[]) => {
      setTableState({ selectedProducts: value });
    },
    [setTableState],
  );

  const setSelectedPlatforms = useCallback(
    (value: string[]) => {
      setTableState({ selectedPlatforms: value });
    },
    [setTableState],
  );

  const setSelectedCampaigns = useCallback(
    (value: string[]) => {
      setTableState({ selectedCampaigns: value });
    },
    [setTableState],
  );

  const setSelectedAds = useCallback(
    (value: string[]) => {
      setTableState({ selectedAds: value });
    },
    [setTableState],
  );

  const [tabState, setTabState] = useQueryParams(
    {
      tabView: withDefault(TabViewParam, "product" as const),
    },
    { updateType: "pushIn" },
  );

  const setAttributionModel = useCallback(
    (value: string) => {
      setFilters({ attributionModel: value });
    },
    [setFilters],
  );

  const setAttributionWindow = useCallback(
    (value: IMCAttributionWindowDays) => {
      setFilters({ attributionWindow: value });
    },
    [setFilters],
  );

  const setAccountingMode = useCallback(
    (value: INBAccountingMode) => {
      setFilters({ accountingMode: value });
    },
    [setFilters],
  );

  const setDateRange = useCallback(
    (value: FixedDateInterval | DateRangeChoiceV3) => {
      setFilters({ dateRange: value });
    },
    [setFilters],
  );

  const setAdvancedSearch = useCallback(
    (value: any | null) => {
      setFilters({ advancedSearch: value });
    },
    [setFilters],
  );

  const setSorting = useCallback(
    (value: Sorting) => {
      setTableState({ sorting: value });
    },
    [setTableState],
  );

  const setMetricFilters = useCallback(
    (value: MetricFilter[]) => {
      setTableState({ metricFilters: value });
    },
    [setTableState],
  );

  const setSelectedChartDots = useCallback(
    (ids: string[]) => {
      setChartSelected(ids);
    },
    [setChartSelected],
  );

  const setChartView = useCallback(
    (value: ChartView) => {
      setChartState({ chartView: value });
    },
    [setChartState],
  );

  const setQuickSelect = useCallback(
    (value: string) => {
      setSelectedQuickSelect({ quickSelect: value });
    },
    [setSelectedQuickSelect],
  );

  const setTabView = useCallback(
    (value: TabView) => {
      setTabState({ tabView: value });
    },
    [setTabState],
  );

  return (
    <ProductAnalyticsContext.Provider
      value={{
        filters: {
          ...filters,
        },
        selectedRelationRow,
        setSelectedRelationRow,
        productItemRelationModalOpen,
        setProductItemRelationModalOpen,
        setSelectedChartDots,
        chartSelected,
        tableState,
        setAttributionModel,
        setAttributionWindow,
        setAccountingMode,
        setAdvancedSearch,
        setSorting,
        setMetricFilters,
        tabState,
        setTabView,
        setDateRange,
        setChartView,
        chartState,
        setSelectedProducts,
        setSelectedPlatforms,
        setSelectedCampaigns,
        setSelectedAds,
        selectedQuickSelect,
        setQuickSelect,
      }}
    >
      {children}
    </ProductAnalyticsContext.Provider>
  );
};
